import { Capacitor } from '@capacitor/core';
import { getUserLocation } from './getUserLocation';

// Best-effort reverse geolocation for the marketplace's
// "Aproape de tine" affordance. Takes raw lat/lon from
// navigator.geolocation and snaps to the closest entry in our
// canonical city list via haversine distance.
//
// We deliberately don't reverse-geocode through Google or Nominatim
// here — the search endpoint matches city by string contains, and
// only the eight canonical names below have salons reliably
// populated against them. A visitor in a small village near
// Cluj-Napoca lands on "Cluj-Napoca" rather than an obscure
// commune name the search would return zero results for.
//
// Returns null when no fix can be derived (shouldn't happen — the
// math always picks SOME city — but kept for callers that want to
// branch on a defensive null check).

const CITY_COORDS = {
  'București':   [44.4268, 26.1025],
  'Cluj-Napoca': [46.7712, 23.6236],
  'Timișoara':   [45.7489, 21.2087],
  'Iași':        [47.1585, 27.6014],
  'Brașov':      [45.6427, 25.5887],
  'Constanța':   [44.1733, 28.6383],
  'Sibiu':       [45.7983, 24.1256],
  'Oradea':      [47.0465, 21.9189],
};

const EARTH_RADIUS_KM = 6371;

export default function snapNearestCity(lat, lon) {
  let best = null;
  let bestDist = Infinity;
  for (const [name, [clat, clon]] of Object.entries(CITY_COORDS)) {
    const dLat = (clat - lat) * Math.PI / 180;
    const dLon = (clon - lon) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat * Math.PI / 180) * Math.cos(clat * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    const dist = 2 * EARTH_RADIUS_KM * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    if (dist < bestDist) { bestDist = dist; best = name; }
  }
  return best;
}

// Promise wrapper around navigator.geolocation.getCurrentPosition.
// Three callsites (LocationPopover, WhereModal, MapPage) used to copy
// the same boilerplate — feature-check, callback-style getCurrentPosition,
// shared `{ timeout, maximumAge }` defaults, error-code branching for
// PERMISSION_DENIED. Lifting it here gives them one async surface and
// removes the temptation to drift the options block.
//
// Resolves to `{ lat, lng, errorCode }`:
//   success → { lat, lng, errorCode: null }
//   failure → { lat: null, lng: null, errorCode: 'unsupported' | 'denied' | 'unavailable' }
// Always resolves, never rejects — callers branch on `errorCode` instead
// of wrapping a try/catch (matches the previous callsites' shape).
//
// Defaults match what the marketplace flow needed (6s timeout, cached
// fix accepted up to 5 minutes old, no high accuracy). MapPage / any
// caller that wants different timeout or precision passes `opts`.
export function getCurrentPositionAsync(opts = {}) {
  // Native (iOS/Android): HTML5 navigator.geolocation is unreliable inside the
  // Capacitor WebView — blocked under capacitor:// on iOS, and on Android it
  // never triggers the OS runtime-permission prompt, so it fails silently and
  // the map falls back to the whole-country view. Route through the native
  // @capacitor/geolocation plugin (getUserLocation), which requests the
  // permission and reads the platform locator. Keep the never-rejects
  // { lat, lng, errorCode } contract every caller depends on.
  if (Capacitor?.isNativePlatform?.()) {
    return getUserLocation()
      .then(({ lat, lng }) => ({ lat, lng, errorCode: null }))
      .catch((err) => ({
        lat: null,
        lng: null,
        errorCode: /denied|permission/i.test(err?.message || '') ? 'denied' : 'unavailable',
      }));
  }
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve({ lat: null, lng: null, errorCode: 'unsupported' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        errorCode: null,
      }),
      (err) => resolve({
        lat: null,
        lng: null,
        errorCode: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable',
      }),
      { timeout: 6000, maximumAge: 5 * 60 * 1000, ...opts },
    );
  });
}

// Convenience: get the current fix, snap it to the nearest canonical
// city. The marketplace's "Aproape de tine" affordances all want this
// combined shape; isolating it keeps the two-step composition out of
// the components that just want a city name back.
export async function geolocateNearestCity(opts = {}) {
  const result = await getCurrentPositionAsync(opts);
  if (result.errorCode) return { ...result, city: null };
  return { ...result, city: snapNearestCity(result.lat, result.lng) };
}
